import { SW_SCOPE } from "@/lib/pwa";

export type UpdateServiceWorker = (reloadPage?: boolean) => Promise<void>;

export interface RegisterPwaCallbacks {
  onNeedRefresh: () => void;
  onOfflineReady: () => void;
  onRegisterError: (error: unknown) => void;
}

export type PwaRegistrationState =
  | "missing"
  | "active"
  | "installing"
  | "waiting"
  | "redundant";

export interface PwaRegistrationSnapshot {
  registration: ServiceWorkerRegistration | null;
  registrationState: PwaRegistrationState;
  workerState: ServiceWorkerState | null;
  controllerPresent: boolean;
}

interface WaitForRegistrationOptions {
  timeoutMs?: number;
  pollIntervalMs?: number;
}

let registerPromise: Promise<UpdateServiceWorker | null> | null = null;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const waitForWorkerStateChange = async (
  worker: ServiceWorker,
  timeoutMs: number,
) => {
  if (timeoutMs <= 0) return;
  await new Promise<void>((resolve) => {
    const timeoutId = setTimeout(() => {
      resolve();
    }, timeoutMs);

    worker.addEventListener(
      "statechange",
      () => {
        clearTimeout(timeoutId);
        resolve();
      },
      { once: true },
    );
  });
};

const resolveRegistrationState = (
  registration: ServiceWorkerRegistration | null,
): { registrationState: PwaRegistrationState; workerState: ServiceWorkerState | null } => {
  if (!registration) {
    return { registrationState: "missing", workerState: null };
  }

  if (registration.active) {
    if (registration.active.state === "redundant") {
      return { registrationState: "redundant", workerState: registration.active.state };
    }

    return { registrationState: "active", workerState: registration.active.state };
  }

  if (registration.waiting) {
    if (registration.waiting.state === "redundant") {
      return { registrationState: "redundant", workerState: registration.waiting.state };
    }

    return { registrationState: "waiting", workerState: registration.waiting.state };
  }

  if (registration.installing) {
    if (registration.installing.state === "redundant") {
      return { registrationState: "redundant", workerState: registration.installing.state };
    }

    return { registrationState: "installing", workerState: registration.installing.state };
  }

  return { registrationState: "missing", workerState: null };
};

export const registerPwaServiceWorker = async (
  callbacks: RegisterPwaCallbacks,
): Promise<UpdateServiceWorker | null> => {
  if (typeof window === "undefined") return null;

  if (!registerPromise) {
    registerPromise = import("virtual:pwa-register")
      .then(({ registerSW }) =>
        registerSW({
          immediate: true,
          onNeedRefresh: callbacks.onNeedRefresh,
          onOfflineReady: callbacks.onOfflineReady,
          onRegisterError: callbacks.onRegisterError,
        }),
      )
      .catch((error) => {
        callbacks.onRegisterError(error);
        return null;
      });
  }

  return registerPromise;
};

export const readPwaRegistrationSnapshot =
  async (): Promise<PwaRegistrationSnapshot> => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return {
        registration: null,
        registrationState: "missing",
        workerState: null,
        controllerPresent: false,
      };
    }

    const registration = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    const { registrationState, workerState } = resolveRegistrationState(registration ?? null);

    return {
      registration: registration ?? null,
      registrationState,
      workerState,
      controllerPresent: Boolean(navigator.serviceWorker.controller),
    };
  };

export const formatPwaRegistrationState = (
  snapshot: Pick<PwaRegistrationSnapshot, "registrationState" | "workerState">,
) =>
  snapshot.workerState
    ? `${snapshot.registrationState}:${snapshot.workerState}`
    : snapshot.registrationState;

export const waitForUsablePwaRegistration = async (
  options: WaitForRegistrationOptions = {},
): Promise<PwaRegistrationSnapshot> => {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    throw new Error("Service worker API is not available.");
  }

  const timeoutMs = options.timeoutMs ?? 10000;
  const pollIntervalMs = options.pollIntervalMs ?? 250;
  const deadline = Date.now() + timeoutMs;
  let snapshot = await readPwaRegistrationSnapshot();

  while (Date.now() < deadline) {
    snapshot = await readPwaRegistrationSnapshot();
    if (
      snapshot.registrationState === "active" ||
      snapshot.registrationState === "waiting"
    ) {
      return snapshot;
    }

    if (snapshot.registrationState === "redundant") {
      throw new Error("Service worker became redundant before activation.");
    }

    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) break;

    const installingWorker = snapshot.registration?.installing;
    if (installingWorker) {
      await waitForWorkerStateChange(
        installingWorker,
        Math.min(remainingMs, pollIntervalMs),
      );
      continue;
    }

    await sleep(Math.min(remainingMs, pollIntervalMs));
  }

  throw new Error(
    `Service worker did not reach a usable state before timeout. Last state: ${formatPwaRegistrationState(snapshot)}.`,
  );
};
